<template>
  <v-form v-model="valid" ref="form" @submit.prevent="onSignup">
    <v-snackbar
      top
      timeout="3000"
      transition="slide-y-transition"
      v-model="alert"
      color="error"
      >{{ alertMsg }}</v-snackbar
    >
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          label="First Name"
          name="firstname"
          required
          :rules="[rules.required]"
          v-model="firstname"
          autocomplete="false"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          label="Last Name"
          name="lastname"
          required
          :rules="[rules.required]"
          v-model="lastname"
          autocomplete="false"
        ></v-text-field>
      </v-col>
      <v-col cols="12">
        <v-row no-gutters>
          <v-col cols="4" md="3">
            <v-select
              v-model="zip"
              :items="countryCodes"
              menu-props="auto"
              label="Zip"
            ></v-select>
          </v-col>
          <v-col cols="8" md="9">
            <v-text-field
              label="Phone Number"
              name="phone"
              required
              :rules="[rules.required, rules.phone]"
              v-model="phone"
              :maxlength="10"
              type="text"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12">
        <v-text-field
          label="Email"
          name="email"
          required
          :rules="[rules.required, rules.email]"
          v-model="email"
          autocomplete="false"
        ></v-text-field>
      </v-col>
      <v-col cols="12">
        <v-text-field
          label="Password"
          name="password"
          required
          :rules="[rules.required, rules.password.min]"
          v-model="password"
          :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
          v-on:click:append="show = !show"
          :type="show ? 'text' : 'password'"
        ></v-text-field>
      </v-col>
      <v-col cols="12">
        <v-text-field
          label="Confirm Password"
          name="cpassword"
          required
          :rules="confirmPasswordRule"
          v-model="cpassword"
          :append-icon="showcp ? 'mdi-eye' : 'mdi-eye-off'"
          v-on:click:append="showcp = !showcp"
          :type="showcp ? 'text' : 'password'"
        ></v-text-field>
      </v-col>

      <v-col cols="12" class="text-center">
        By signing up, you agree to our
        <a href="#" target="_blank" class="sokoDeep--text">Terms of Service</a>
        and
        <a href="#" target="_blank" class="sokoDeep--text">Privacy Policy</a>
      </v-col>

      <v-col cols="12">
        <v-btn
          block
          large
          rounded
          depressed
          :loading="loading"
          :disabled="!valid"
          class="sokoDeep"
          :dark="valid"
          type="submit"
          >Sign up</v-btn
        >
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { mapActions } from "vuex";
import countryCodes from "@/externals/data/CountryCodes.js";
import pnf from "@/externals/logic/PhoneNumberFormatter.js";

export default {
  name: "Signup",
  props: {
    rules: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    firstname: "", // firstname
    lastname: "", // lastname
    email: "", // email
    zip: "+233",
    phone: "", // phone number
    password: "", // password
    cpassword: "", // confirm password

    loading: false, // form button loading status
    show: false, // password visibility status
    showcp: false, // confirm password visibility status
    valid: true, // form validity
    alert: false, // alert visisbility
    alertMsg: "Error!", // alert message
    networkError: false, // error while logging in is a network error
    countryCodes,
  }),
  computed: {
    confirmPasswordRule() {
      // confirm password validation rule
      return [
        (v) => !!v || "Please confirm your password",
        (v) => v === this.password || "Passwords must match.",
      ];
    },
  },
  methods: {
    ...mapActions(["createUser", "authenticateUser"]),
    async onSignup() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        const credentials = {
          fname: this.firstname,
          lname: this.lastname,
          phonenumber: pnf.structure(this.zip, this.phone),
          email: this.email,
          password: this.password,
        };

        // Hit the create user route and get the user's details if signup is successful, returns null on failure but json of the user's details on success
        const jsonRes = await this.createUser(credentials).catch((err) => {
          console.log(err);
          this.networkError = true;
          return false;
        });

        if (this.networkError) {
          // if a network error occured
          this.alertMsg =
            "No connection was established to sign you up. Please check your internet connection and try again!";
          this.alert = true;
          // reset network error status
          this.networkError = false;
          // stop loading animation
          this.loading = false;
          return;
        } else if (jsonRes) {
          // if a response came from the server
          if (jsonRes.uid) {
            // user was successfully created
            this.$emit("signupIsSuccess");

            // log user in
            const isLoggedIn = this.authenticateUser({
              username: this.email,
              password: this.password,
            }).catch((err) => {
              console.log(err);
              return false;
            });

            if (isLoggedIn) {
              console.log("Login successful");
            } else {
              this.$emit("loginFailure");
            }
          } else {
            // an error occured while creating the user
            // display appropriate error message on failure
            this.alertMsg = jsonRes.message || jsonRes.error.message;
            this.alert = true;
          }
        } else {
          // error was not captureda
          this.alertMsg =
            "OOps! An unexpected error occured while signing you up. Please try again later!";
          this.alert = true;
        }
        // stop loading animation
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
a,
a:hover,
a:active {
  text-decoration: none;
}
</style>
